// Лента: работа агента рассказом по времени (DESIGN §6 «Лента агента»). Сначала рассказ агента,
// потом что он записал (заметки, решения, советы, проверки), и только по кнопке — сырые шаги.
import { Button, Chip, EmptyState, type Tone, till, toast, when } from '@autoaff/ui';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarClock, HelpCircle, ListTree, MessageCircle, Play, RotateCcw, Rss } from 'lucide-react';
import { memo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { type FeedRun, type SchedulesResponse, waitReason } from '../../agent-api';
import { api } from '../../api';
import { chatStore } from '../../chat/store';
import { askOfRun } from '../../shell/waiting';
import { useAgentStatus } from '../../streams';

const STATUS: Record<FeedRun['status'], { text: string; tone: Tone }> = {
  queued: { text: 'В очереди', tone: 'default' },
  deferred: { text: 'Отложено', tone: 'warning' },
  starting: { text: 'Запускается', tone: 'accent' },
  running: { text: 'Работает', tone: 'accent' },
  completed: { text: 'Готово', tone: 'success' },
  interrupted: { text: 'Прервано', tone: 'warning' },
  failed: { text: 'Не удалось', tone: 'danger' },
  cancelled: { text: 'Отменено', tone: 'default' },
};

export const Md = memo(function Md({ text }: { text: string }) {
  return (
    <div className="md">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{ a: ({ node: _n, ...p }) => <a {...p} target="_blank" rel="noreferrer" /> }}
      >
        {text}
      </Markdown>
    </div>
  );
});

function plural(n: number, one: string, few: string, many: string) {
  const a = n % 100;
  const b = n % 10;
  return a > 10 && a < 20 ? many : b === 1 ? one : b > 1 && b < 5 ? few : many;
}

function Artifacts({ a }: { a: NonNullable<FeedRun['artifacts']> }) {
  const parts = [
    a.notes && `${a.notes} ${plural(a.notes, 'заметка', 'заметки', 'заметок')}`,
    a.decisions && `${a.decisions} ${plural(a.decisions, 'решение', 'решения', 'решений')}`,
    a.proposals && `${a.proposals} ${plural(a.proposals, 'совет', 'совета', 'советов')}`,
    a.followups && `${a.followups} ${plural(a.followups, 'проверка', 'проверки', 'проверок')} на потом`,
  ].filter(Boolean);
  if (!parts.length) return null;
  return <p className="feed-art">Записал: {parts.join(' · ')}</p>;
}

function RunCard({ r, onChanged }: { r: FeedRun; onChanged: () => void }) {
  const [full, setFull] = useState(false);
  const s = STATUS[r.status];
  const wait = r.status === 'queued' || r.status === 'deferred' ? waitReason(r) : null;
  const live = r.status === 'running' || r.status === 'starting';
  const open = (draft?: string) =>
    r.threadId && chatStore.open({ threadId: r.threadId, draft: draft ?? null });
  const cancel = async () => {
    try {
      await api(`/api/agent/runs/${r.id}/cancel`, {});
      toast('Работа отменена');
      onChanged();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const resume = async () => {
    try {
      await api(`/api/agent/runs/${r.id}/continue`, {});
      toast('Агент продолжит с того места, где остановился');
      onChanged();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  // Оборвалась (сбой, перезапуск) или это работа над роликами, которую можно доделать
  const canResume =
    ['failed', 'interrupted'].includes(r.status) || (r.kind === 'creative' && r.status === 'completed');
  const long = (r.report?.summary.length ?? 0) > 700;
  return (
    <article className="card feed-card">
      <header className="feed-head">
        <Chip tone={r.kind === 'event' ? 'warning' : 'accent'}>{r.kindLabel}</Chip>
        <Chip tone={s.tone} dot={live}>
          {s.text}
        </Chip>
        <time className="muted" dateTime={r.startedAt ?? r.createdAt}>
          {when(r.startedAt ?? r.scheduledFor ?? r.createdAt)}
        </time>
      </header>
      {r.report ? (
        <>
          <h3 className="feed-title">{r.report.headline}</h3>
          <div className={long && !full ? 'feed-clip' : undefined}>
            <Md text={r.report.summary} />
          </div>
          {long && (
            <button type="button" className="link-btn" onClick={() => setFull((x) => !x)}>
              {full ? 'Свернуть' : 'Читать полностью'}
            </button>
          )}
          {r.report.asks_owner.length > 0 && (
            <div className="feed-asks">
              <b>
                <HelpCircle size={16} /> Вопросы к вам
              </b>
              <ul>
                {r.report.asks_owner.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
              <Button
                size="sm"
                variant="primary"
                icon={<MessageCircle />}
                onClick={() => {
                  // Разговор этой работы с закреплённым вопросом: первое сообщение — ответ на него
                  const ask = askOfRun(r);
                  if (ask) chatStore.open({ ask });
                }}
              >
                Ответить агенту
              </Button>
            </div>
          )}
        </>
      ) : (
        <p className="muted feed-wait">
          {live
            ? 'Агент работает — рассказ появится, когда он закончит.'
            : wait
              ? wait
              : r.status === 'failed'
                ? 'Агент не смог закончить эту работу после нескольких попыток.'
                : 'Итога нет.'}
        </p>
      )}
      {r.artifacts && <Artifacts a={r.artifacts} />}
      <footer className="feed-foot">
        {r.threadId && (
          <Button size="sm" variant="ghost" icon={<ListTree />} onClick={() => open()}>
            {live ? 'Смотреть, как работает' : 'Показать шаги'}
          </Button>
        )}
        {r.report && r.threadId && (
          <Button size="sm" variant="ghost" icon={<MessageCircle />} onClick={() => open()}>
            Спросить об этом
          </Button>
        )}
        {(r.status === 'queued' || r.status === 'deferred') && (
          <Button size="sm" variant="ghost" onClick={cancel} style={{ marginLeft: 'auto' }}>
            Отменить
          </Button>
        )}
        {canResume && (
          <Button
            size="sm"
            variant={r.status === 'failed' ? 'primary' : 'ghost'}
            icon={<RotateCcw />}
            onClick={resume}
            style={{ marginLeft: 'auto' }}
          >
            Продолжить работу
          </Button>
        )}
      </footer>
    </article>
  );
}

function QueueLine() {
  const { status } = useAgentStatus();
  const q = status?.queue;
  if (!q || (!q.waiting && !q.background)) return null;
  const until = q.limitUntil ? till(q.limitUntil) : null;
  return (
    <p className="feed-queue">
      {q.background > 0 && <span>Сейчас работает: {q.background}</span>}
      {q.waiting > 0 && <span>В очереди: {q.waiting}</span>}
      {until && <span className="warn">Лимит подписки — фоновая работа продолжится в {until}</span>}
    </p>
  );
}

export function FeedTab() {
  const qc = useQueryClient();
  const q = useInfiniteQuery({
    queryKey: ['agent', 'feed'],
    queryFn: ({ pageParam }) =>
      api<FeedRun[]>(
        `/api/agent/feed?limit=20${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => (last.length === 20 ? last[last.length - 1]?.createdAt : undefined),
    refetchInterval: 15_000,
  });
  const sch = useQuery({
    queryKey: ['agent', 'schedules'],
    queryFn: () => api<SchedulesResponse>('/api/agent/schedules'),
  });
  const round = sch.data?.schedules.find((s) => s.kind === 'round');
  const runNow = async () => {
    if (!round) return;
    try {
      await api(`/api/agent/schedules/${round.id}/run`, {});
      toast('Обход поставлен в очередь');
      void qc.invalidateQueries({ queryKey: ['agent', 'feed'] });
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const items = q.data?.pages.flat() ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: ['agent', 'feed'] });

  return (
    <div className="pc feed">
      <div className="feed-top">
        <QueueLine />
        {round && (
          <Button size="sm" icon={<Play />} onClick={runNow} style={{ marginLeft: 'auto' }}>
            Запустить обход сейчас
          </Button>
        )}
      </div>
      {q.isPending && [0, 1].map((i) => <div key={i} className="card skeleton" style={{ height: 200 }} />)}
      {q.isError && (
        <div className="card">
          <EmptyState
            icon={<Rss />}
            title="Не удалось загрузить ленту"
            action={<Button onClick={() => q.refetch()}>Повторить</Button>}
          >
            {(q.error as Error).message}
          </EmptyState>
        </div>
      )}
      {q.isSuccess && items.length === 0 && (
        <div className="card">
          <EmptyState
            icon={<Rss />}
            title="Агент ещё не работал сам"
            action={
              <Link to="/agent/tasks" hash="schedule" className="btn">
                <CalendarClock /> Открыть расписание
              </Link>
            }
          >
            По расписанию он проснётся в 09:00 (брифинг), 13:00 и 17:00 (обходы) и в 21:00 (итог дня) по
            Ташкенту, а ещё — когда страховка заметит тревогу или подойдёт время проверки, которую он назначил
            себе сам. Каждая работа появится здесь рассказом: что увидел, что сделал и почему.
          </EmptyState>
        </div>
      )}
      {items.map((r) => (
        <RunCard key={r.id} r={r} onChanged={refresh} />
      ))}
      {q.hasNextPage && (
        <Button
          onClick={() => q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
          style={{ alignSelf: 'center' }}
        >
          Показать раньше
        </Button>
      )}
    </div>
  );
}
